// Client store of the account's boutiques (GET /shops/mine -> User.boutiques),
// shared by every switcher / "Create New Shop" button of the page so they
// always agree. Changing the active shop re-reads the server answer and
// broadcasts `elakay:active-shop-changed` so the pages reload their data
// (products, orders, storefront) for the selected shop.
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { getAccountShops, setActiveAccountShop } from "../auth/apiShops";
import { isAuthenticated } from "../auth";

export const ACTIVE_SHOP_EVENT = "elakay:active-shop-changed";

let state = { status: "idle", data: null, error: null };
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const setState = (next) => { state = { ...state, ...next }; emit(); };

export const subscribeAccountShops = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getAccountShopsSnapshot = () => state;

let inflight = null;
let generation = 0;
const accountKey = () => String(isAuthenticated()?.user?._id || "");
export const loadAccountShops = ({ force = false } = {}) => {
  if (!isAuthenticated()?.token) return Promise.resolve(null);
  const key = accountKey();
  if (state.accountKey !== key) {
    generation++; inflight = null;
    setState({ accountKey: key, status: "idle", data: null, error: null });
  }
  if (inflight) return inflight;
  if (!force && state.status === "ready") return Promise.resolve(state.data);
  setState({ status: state.data ? "refreshing" : "loading", error: null });
  const version = ++generation;
  inflight = getAccountShops()
    .then((data) => { if (version === generation && key === accountKey()) setState({ status: "ready", data, error: null }); return data; })
    .catch((error) => { if (version === generation && key === accountKey()) setState({ status: "error", error }); return null; })
    .finally(() => { if (version === generation) inflight = null; });
  return inflight;
};

export const selectAccountShop = async (type, shopId) => {
  const key = accountKey(), version = ++generation;
  inflight = null;
  const data = await setActiveAccountShop(type, shopId);
  if (key !== accountKey() || version !== generation) return null;
  setState({ accountKey: key, status: "ready", data, error: null });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(ACTIVE_SHOP_EVENT, { detail: { type, shopId: data?.active?.[type] || null } }));
  }
  return data;
};

// After a creation (or a payment confirmation): fresh data, optionally switch.
export const afterShopCreated = async (shop, { activate = false } = {}) => {
  if (activate && shop?._id && !shop.pending) return selectAccountShop(shop.type, shop._id);
  return loadAccountShops({ force: true });
};

export const shopsOfType = (data, type) => (data?.shops || []).filter((shop) => shop.type === type);
export const activeShopOf = (data, type) => {
  const id = data?.active?.[type];
  return shopsOfType(data, type).find((shop) => String(shop._id) === String(id)) || null;
};

export const useAccountShops = ({ enabled = true } = {}) => {
  const snapshot = useSyncExternalStore(subscribeAccountShops, getAccountShopsSnapshot, getAccountShopsSnapshot);
  const key = accountKey();
  useEffect(() => { if (enabled) loadAccountShops(); }, [enabled, key]);
  const reload = useCallback(() => loadAccountShops({ force: true }), []);
  return { ...snapshot, data: snapshot.accountKey === key ? snapshot.data : null, reload };
};

// Re-runs `callback` whenever the active shop of `type` changes.
export const useActiveShopChange = (type, callback) => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => { if (!type || event?.detail?.type === type) callback(event.detail); };
    window.addEventListener(ACTIVE_SHOP_EVENT, handler);
    return () => window.removeEventListener(ACTIVE_SHOP_EVENT, handler);
  }, [type, callback]);
};

export const __resetAccountShopsStore = (data = null) => {
  generation++;
  state = { accountKey: accountKey(), status: data ? "ready" : "idle", data, error: null };
  inflight = null;
  emit();
};
